import React from "react";
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";
import {
  elderRay,
  ema,
  discontinuousTimeScaleProviderBuilder,
  Chart,
  ChartCanvas,
  CurrentCoordinate,
  BarSeries,
  CandlestickSeries,
  ElderRaySeries,
  LineSeries,
  MovingAverageTooltip,
  OHLCTooltip,
  SingleValueTooltip,
  lastVisibleItemBasedZoomAnchor,
  XAxis,
  YAxis,
  CrossHairCursor,
  EdgeIndicator,
  MouseCoordinateX,
  MouseCoordinateY,
  ZoomButtons,
} from "react-financial-charts";
import AutoSizer from "react-virtualized-auto-sizer";
import { isMobile } from "react-device-detect";
import useTheme from "../../context/ThemeProvider";

/**
 * This function receives the graphic data from the backend
 * and generate a technical chart.
 * @param {array} data
 * @returns {JSX.Element}
 */
export function GraphicChart(junkData) {
  const { theme } = useTheme();

  if (!junkData) {
    return null;
  }

  const chartData = parseDataTypes(junkData);

  const priceMarginRight = calculateMarginRight(chartData);

  const ScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor(
      (d) => new Date(d.date)
    );
  const height = isMobile ? 600 : 650;
  const margin = { left: 10, right: priceMarginRight, top: 0, bottom: 24 };

  const ema12 = ema()
    .id(1)
    .options({ windowSize: 12 })
    .merge((d, c) => {
      d.ema12 = c;
    })
    .accessor((d) => d.ema12);

  const ema26 = ema()
    .id(2)
    .options({ windowSize: 26 })
    .merge((d, c) => {
      d.ema26 = c;
    })
    .accessor((d) => d.ema26);

  const elder = elderRay();

  const calculatedData = elder(ema26(ema12(chartData)));
  const { data, xScale, xAccessor, displayXAccessor } =
    ScaleProvider(calculatedData);
  const pricesDisplayFormat = format(".2f");
  const max = xAccessor(data[data.length - 1]);
  const min = xAccessor(data[Math.max(0, data.length - 100)]);
  const xExtents = [min, max + 5];

  const gridHeight = height - margin.top - margin.bottom;

  const elderRayHeight = 100;
  const elderRayOrigin = (_, h) => [0, h - elderRayHeight];
  const barChartHeight = gridHeight / 4;
  const barChartOrigin = (_, h) => [0, h - barChartHeight - elderRayHeight];
  const chartHeight = gridHeight - elderRayHeight;
  const dateTimeFormat = "%d %b";
  const timeDisplayFormat = timeFormat(dateTimeFormat);

  const barChartExtents = (data) => {
    return data.volume;
  };

  const candleChartExtents = (data) => {
    return [data.high, data.low];
  };

  const yEdgeIndicator = (data) => {
    return data.close;
  };

  const volumeColor = (data) => {
    return data.close > data.open
      ? "rgba(38, 166, 154, 0.3)"
      : "rgba(239, 83, 80, 0.3)";
  };

  const volumeSeries = (data) => {
    return data.volume;
  };

  const openCloseColor = (data) => {
    return data.close > data.open ? "#26a69a" : "#ef5350";
  };

  const gridLinesStrokeStyle = () => {
    return theme === "dark"
      ? "#f5f5f522"
      : "#E2E4EC";
  }

  const strokeStyle = () => {
    return theme === "dark" ? "#E2E4EC" : "#000000";
  }

  const tickLabelFill = () => {
    return theme === "dark" ? "#E2E4EC" : "#000000";
  }

  return (
    <div
      style={
        isMobile
          ? { width: "100%", height: 500 }
          : { width: "100%", height: 595 }
      }
    >
      <AutoSizer>
        {({ height, width }) => (
          <ChartCanvas
            height={height}
            ratio={3}
            width={width}
            margin={margin}
            data={data}
            displayXAccessor={displayXAccessor}
            seriesName="Data"
            xScale={xScale}
            xAccessor={xAccessor}
            xExtents={xExtents}
            zoomAnchor={lastVisibleItemBasedZoomAnchor}
          >
            <Chart
              id={2}
              height={barChartHeight}
              origin={barChartOrigin}
              yExtents={barChartExtents}
            >
              <BarSeries fillStyle={volumeColor} yAccessor={volumeSeries} />

              <SingleValueTooltip
                yLabel="Volume"
                yAccessor={(d) => d.volume}
                origin={[8, 16]}
                valueFill={tickLabelFill()}
              />
            </Chart>

            <Chart id={3} height={chartHeight} yExtents={candleChartExtents}>
              <XAxis showGridLines showTickLabel={false} tickLabelFill={tickLabelFill()} gridLinesStrokeStyle={gridLinesStrokeStyle()} />
              <YAxis showGridLines tickFormat={pricesDisplayFormat} tickLabelFill={tickLabelFill()} strokeStyle={strokeStyle()} gridLinesStrokeStyle={gridLinesStrokeStyle()}  />
              <CandlestickSeries />
              <LineSeries
                yAccessor={ema26.accessor()}
                strokeStyle={ema26.stroke()}
              />
              <CurrentCoordinate
                yAccessor={ema26.accessor()}
                fillStyle={ema26.stroke()}
              />
              <LineSeries
                yAccessor={ema12.accessor()}
                strokeStyle={ema12.stroke()}
              />
              <CurrentCoordinate
                yAccessor={ema12.accessor()}
                fillStyle={ema12.stroke()}
              />
              <MouseCoordinateY
                rectWidth={margin.right}
                displayFormat={pricesDisplayFormat}
              />
              <EdgeIndicator
                itemType="last"
                rectWidth={margin.right}
                fill={openCloseColor}
                lineStroke={openCloseColor}
                displayFormat={pricesDisplayFormat}
                yAccessor={yEdgeIndicator}
              />
              <MovingAverageTooltip
                origin={[8, 24]}
                options={[
                  {
                    yAccessor: ema26.accessor(),
                    type: "EMA",
                    stroke: ema26.stroke(),
                    windowSize: ema26.options().windowSize,
                  },
                  {
                    yAccessor: ema12.accessor(),
                    type: "EMA",
                    stroke: ema12.stroke(),
                    windowSize: ema12.options().windowSize,
                  },
                ]}
                textFill={tickLabelFill()}
              />

              <ZoomButtons />
              <OHLCTooltip origin={[8, 16]} textFill={tickLabelFill()} />
            </Chart>

            <Chart
              id={4}
              height={elderRayHeight}
              yExtents={[0, elder.accessor()]}
              origin={elderRayOrigin}
              padding={{ top: 8, bottom: 8 }}
            >
              <XAxis showGridLines gridLinesStrokeStyle={gridLinesStrokeStyle()} tickLabelFill={tickLabelFill()} strokeStyle={strokeStyle()} />

              <MouseCoordinateX displayFormat={timeDisplayFormat} />
              <MouseCoordinateY
                rectWidth={margin.right}
                displayFormat={pricesDisplayFormat}
              />

              <ElderRaySeries
                yAccessor={elder.accessor()}
                fillStyle={{
                  bearPower: "rgba(248, 187, 208, 0.2)",
                  bullPower: "rgba(159, 168, 218, 0.2)",
                }}
              />

              <SingleValueTooltip
                yAccessor={elder.accessor()}
                yLabel="Elder-Ray"
                yDisplayFormat={(d) =>
                  `${pricesDisplayFormat(d.bullPower)}, ${pricesDisplayFormat(
                    d.bearPower
                  )}`
                }
                origin={[8, 16]}
                valueFill={tickLabelFill()}
              />
            </Chart>

            <CrossHairCursor />
          </ChartCanvas>
        )}
      </AutoSizer>
    </div>
  );
}

/**
 * the graph data is from third party providers,
 * for some reason they pass this data in string,
 * so this function organizes this data into their proper types
 * We do it in the client side for save backend processing.
 * @param {arrray} data
 * @returns {array}
 */
function parseDataTypes(data) {
  var response = [];

  data.forEach((item) => {
    let parseData = () => parseInt(item.start_timestamp * 1000);
    response.push({
      date: new Date(parseData()),
      open: parseFloat(item.open),
      high: parseFloat(item.max),
      low: parseFloat(item.min),
      close: parseFloat(item.close),
      volume: parseInt(item.volume),
    });
  });

  return response.reverse();
}

/**
 * Clculates the margin in pixels, according to the price digits.
 * @param {array} data
 */
const calculateMarginRight = (junkedData) => {
  let lastPriceClose = junkedData.at(-1).close;
  let qntDigitsInPrice = lastPriceClose.toString().length;
  let margin = 15;
  let lastPriceCloseString = lastPriceClose.toString();

  //Check if the price contains "." and count the digits before the dot.
  if (lastPriceCloseString.includes(".")) {
    let qntDigitsBeforeDot = lastPriceCloseString.split(".");
    qntDigitsBeforeDot = qntDigitsBeforeDot[0].length;
    //if it is an forex.
    if (qntDigitsBeforeDot === 1) {
      margin = margin + 20;
    } else {
      for (let i = 0; i < qntDigitsBeforeDot; i++) {
        if (i < 2) {
          margin = margin + 15;
        } else {
          margin = margin + 9;
        }
      }
    }
  } else {
    //But else, let's count all digits.
    for (let i = 0; i < qntDigitsInPrice; i++) {
      if (i < 2) {
        margin = margin + 14;
      } else {
        margin = margin + 9;
      }
    }
  }

  return margin;
};
